import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { Link } from './Link';
import styles from './Breadcrumb.module.css';

export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  items: readonly BreadcrumbItem[];
  /** Names the trail when more than one exists on a page. */
  label?: string;
}

/**
 * The drill path (method → stage → activity). The last item is the current page
 * and is never a link: `aria-current="page"` plus plain text, so a keyboard user
 * does not tab onto a link that goes nowhere.
 */
export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(function Breadcrumb(
  { items, label = 'Breadcrumb', className, ...rest },
  ref,
) {
  return (
    <nav {...rest} ref={ref} aria-label={label} className={clsx(styles.nav, className)}>
      <ol className={styles.list}>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li className={styles.item} key={index}>
              {index > 0 && (
                <span className={styles.separator} aria-hidden="true">
                  /
                </span>
              )}
              {last ? (
                <span className={styles.current} aria-current="page">
                  {item.label}
                </span>
              ) : (
                <Link
                  size="sm"
                  href={item.href}
                  className={styles.link}
                  onClick={
                    item.onClick === undefined
                      ? undefined
                      : (event) => {
                          if (item.href === undefined) event.preventDefault();
                          item.onClick?.();
                        }
                  }
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});
